import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Center, Checkbox, Group, Loader, Menu, Modal, Stack, Table, Tabs, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, benchmarkApi, objectsApi, type Benchmark, type BenchmarkInput, type BenchmarkObject } from '../../api';
import { formatAmount } from '../../format';
import { FileDrop } from '../../ui/FileDrop';
import { LibraryTable, type LibraryColumn } from '../../ui/LibraryTable';
import { benchmarkFields } from './benchmarkDetail';
import { parsePastedBenchmarks } from './benchmarkPaste';
import { LexiconTab } from './LexiconTab';
import { PriceIndexTab, RegionalFactorsTab } from './ReferenceTabs';
import { BenchmarkFilterBar } from './BenchmarkFilterBar';
import { BenchmarkObjects, ObjectsWithoutBenchmarks } from './BenchmarkObjects';
import { objectsImportMessage } from './benchmarkObjects';
import { type BenchmarkFilter, NO_FILTER, matches } from './benchmarkFilter';

/**
 * The office's benchmark catalog: look up, paste in, correct (in the popup of
 * a row, AP-128), remove - and, for administrators, who else may use it. The figures come from the user's own
 * licensed sources (e.g. BKI); PST neither fetches nor ships any.
 */
export function BenchmarkCatalog({ onClose, isAdmin, canEdit }: { onClose: () => void; isAdmin: boolean; canEdit: boolean }): JSX.Element {
  return (
    <Modal opened onClose={onClose} title="Kennwertkatalog" size="90%">
      <BenchmarkCatalogBody isAdmin={isAdmin} canEdit={canEdit} />
    </Modal>
  );
}

/** The catalog itself - in the dialog from the cost plan and on the page Verwaltung › Bibliotheken. */
export function BenchmarkCatalogBody({ isAdmin, canEdit }: { isAdmin: boolean; canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['benchmarks'], queryFn: benchmarkApi.list });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['benchmarks'] });
  const update = useMutation({ mutationFn: ({ id, ...body }: { id: string } & Partial<BenchmarkInput>) => benchmarkApi.update(id, body), onSettled: refresh });
  // Removed ones leave the selection; the rest of it stays.
  const remove = useMutation({ mutationFn: (ids: string[]) => benchmarkApi.remove(ids), onSuccess: (_r, ids) => setSelected((s) => new Set([...s].filter((x) => !ids.includes(x)))), onSettled: refresh });
  const add = useMutation({ mutationFn: (rows: BenchmarkInput[]) => benchmarkApi.add(rows), onSuccess: () => setText(''), onSettled: refresh });
  const [filter, setFilter] = useState<BenchmarkFilter>(NO_FILTER);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [defaults, setDefaults] = useState({ source: 'BKI', edition: '', priceDate: '', region: '', buildingType: '', license: '' });
  const error = update.error ?? remove.error ?? add.error;

  const shown = (list.data ?? []).filter((b) => matches(b, filter));
  const paste = text.trim()
    ? parsePastedBenchmarks(text, Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, v.trim() || null])) as Partial<BenchmarkInput>)
    : null;
  const toggle = (id: string): void => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const small = (v: string | null, dimmed = false): JSX.Element => <Text size="xs" c={dimmed ? 'dimmed' : undefined}>{v}</Text>;
  const amount = (v: number | null, bold = false): JSX.Element => <Text size="sm" fw={bold ? 600 : undefined}>{formatAmount(v)}</Text>;
  // Ticking (for deleting several at once) is the administration's; a click on the row only selects it.
  const columns: LibraryColumn<Benchmark>[] = [
    ...(canEdit ? [{
      key: 'tick', title: 'Ankreuzen', defaultWidth: 36, minWidth: 30,
      header: <Checkbox size="xs" aria-label="alle ankreuzen" checked={shown.length > 0 && shown.every((b) => selected.has(b.id))} onChange={(e) => setSelected(e.currentTarget.checked ? new Set(shown.map((b) => b.id)) : new Set())} />,
      render: (b: Benchmark) => <Checkbox size="xs" aria-label={`${b.label} ankreuzen`} checked={selected.has(b.id)} onChange={() => toggle(b.id)} />,
    }] : []),
    { key: 'costGroup', title: 'KG', defaultWidth: 70, render: (b) => <Text size="sm" ff="monospace">{b.costGroup}</Text> },
    {
      key: 'label', title: 'Bezeichnung', defaultWidth: 320, minWidth: 80,
      render: (b) => (
        <>
          <Text size="sm">{b.label}</Text>
          {b.description && <Text size="xs" c="dimmed" lineClamp={1}>{b.description.split('\n')[0]}</Text>}
        </>
      ),
    },
    { key: 'buildingType', title: 'Gebäudeart', defaultWidth: 160, render: (b) => small(b.buildingType) },
    { key: 'source', title: 'Quelle', defaultWidth: 90, render: (b) => small(b.source) },
    { key: 'edition', title: 'Ausgabe', defaultWidth: 80, render: (b) => small(b.edition) },
    { key: 'priceDate', title: 'Preisstand', defaultWidth: 90, render: (b) => small(b.priceDate) },
    { key: 'region', title: 'Region', defaultWidth: 90, render: (b) => small(b.region) },
    { key: 'unit', title: 'Einheit', defaultWidth: 70, render: (b) => small(b.unit) },
    { key: 'low', title: 'von', defaultWidth: 90, align: 'right', render: (b) => amount(b.low) },
    { key: 'mid', title: 'Mittel', defaultWidth: 90, align: 'right', render: (b) => amount(b.mid, true) },
    { key: 'high', title: 'bis', defaultWidth: 90, align: 'right', render: (b) => amount(b.high) },
    { key: 'license', title: 'Lizenz', defaultWidth: 120, render: (b) => small(b.license, true) },
  ];
  const field = (key: keyof typeof defaults, label: string, placeholder: string, w = 140): JSX.Element => (
    <TextInput size="xs" label={label} placeholder={placeholder} value={defaults[key]} w={w} onChange={(e) => { const v = e.currentTarget.value; setDefaults((d) => ({ ...d, [key]: v })); }} />
  );
  const readFile = (file: File | undefined): void => {
    if (!file) return;
    void file.text().then(setText);
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Kennwerte des Büros für alle Projekte, z. B. aus BKI. Sie werden von Hand oder per Einfügen aus Excel/CSV eingepflegt, im Rahmen der eigenen Lizenz; PST ruft nichts selbst ab. In einer Position über ⌕ neben dem Kennwert übernehmen.
      </Text>
      {error && <Alert color="red" py={6} withCloseButton onClose={() => [update, remove, add].forEach((m) => m.reset())}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}
      <Tabs defaultValue="list" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="list">Kennwerte{list.data ? ` (${list.data.length})` : ''}</Tabs.Tab>
          {canEdit && <Tabs.Tab value="import">Einfügen</Tabs.Tab>}
          <Tabs.Tab value="regional">Regionalfaktoren</Tabs.Tab>
          <Tabs.Tab value="index">Baupreisindex</Tabs.Tab>
          <Tabs.Tab value="lexicon">Baupreislexikon</Tabs.Tab>
          {isAdmin && <Tabs.Tab value="access">Freigabe</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="list" pt="sm">
          <Group gap="sm" mb="xs" align="flex-start">
            <div style={{ flex: 1 }}><BenchmarkFilterBar list={list.data ?? []} filter={filter} onChange={setFilter} /></div>
            {list.data && <Text size="xs" c="dimmed" pt={6}>{shown.length.toLocaleString('de-DE')} von {list.data.length.toLocaleString('de-DE')}</Text>}
          </Group>
          <Group mb="xs">
            {filter.buildingType && <BenchmarkObjects key={filter.buildingType} buildingType={filter.buildingType} />}
            {canEdit && selected.size > 0 && (
              <Button size="xs" color="red" variant="light" loading={remove.isPending} onClick={() => { if (window.confirm(`${selected.size} Kennwerte löschen? Positionen, die sie verwenden, behalten ihren Wert.`)) remove.mutate([...selected]); }}>
                {selected.size} löschen
              </Button>
            )}
          </Group>
          {list.data && !filter.buildingType && <ObjectsWithoutBenchmarks benchmarkTypes={list.data.map((b) => b.buildingType)} />}
          {list.isPending && <Center h={80}><Loader size="sm" /></Center>}
          {list.isError && <Alert color="red">{list.error instanceof ApiError ? list.error.message : 'Der Katalog konnte nicht geladen werden.'}</Alert>}
          {list.data && list.data.length === 0 && <Text size="sm" c="dimmed">Noch keine Kennwerte. Unter „Einfügen“ eine Tabelle aus Excel einfügen.</Text>}
          {shown.length > 0 && (
            <LibraryTable<Benchmark>
              tableId="kennwerte"
              label="Kennwerte"
              columns={columns}
              rows={shown}
              rowKey={(b) => b.id}
              rowLabel={(b) => b.label}
              canManage={canEdit}
              fields={benchmarkFields}
              detailExtra={(b) => b.buildingType && <BenchmarkObjects key={b.buildingType} buildingType={b.buildingType} open />}
              edit={async (b, changes) => { await update.mutateAsync({ id: b.id, ...(changes as Partial<BenchmarkInput>) }); }}
              remove={{
                label: () => 'Kennwert löschen …',
                question: () => 'Diesen Kennwert löschen? Positionen, die ihn verwenden, behalten ihren Wert.',
                run: (b) => remove.mutate([b.id]),
              }}
              extraMenu={(b, close) => {
                const run = (f: () => void) => () => { close(); f(); };
                const many = selected.size > 1 && selected.has(b.id);
                return (
                  <>
                    <Menu.Item onClick={run(() => { if (window.confirm('Die Werte von, Mittel und bis dieses Kennwerts löschen? Das lässt sich nicht rückgängig machen.')) update.mutate({ id: b.id, low: null, mid: null, high: null }); })}>Werte von / Mittel / bis löschen …</Menu.Item>
                    {many && <Menu.Item color="red" onClick={run(() => { if (window.confirm(`${selected.size} Kennwerte löschen? Positionen, die sie verwenden, behalten ihren Wert.`)) remove.mutate([...selected]); })}>{selected.size} gewählte löschen …</Menu.Item>}
                  </>
                );
              }}
              maxHeight={520}
              resetKey={JSON.stringify(filter)}
            />
          )}
        </Tabs.Panel>

        {canEdit && (
          <Tabs.Panel value="import" pt="sm">
            <Stack gap="xs">
              <Text size="sm">
                Tabelle aus Excel kopieren und hier einfügen, oder eine CSV-Datei (Semikolon) wählen. Die erste Zeile nennt die Spalten, Reihenfolge beliebig:
                <Text span ff="monospace" size="xs"> KG · Bezeichnung · Einheit · von · Mittel · bis</Text>, dazu wahlweise
                <Text span ff="monospace" size="xs"> Quelle · Ausgabe · Preisstand · Region · Gebäudeart · Lizenz · Bemerkung</Text>.
              </Text>
              <Group gap="xs" align="flex-end" wrap="wrap">
                {field('source', 'Quelle', 'BKI', 100)}
                {field('edition', 'Ausgabe/Jahr', 'z. B. 2024', 110)}
                {field('priceDate', 'Preisstand', 'z. B. 1. Quartal 2024', 150)}
                {field('region', 'Region', 'z. B. Bundesdurchschnitt', 170)}
                {field('buildingType', 'Gebäudeart', 'z. B. Bürogebäude, mittlerer Standard', 240)}
                {field('license', 'Lizenzkennzeichnung', 'z. B. Einzelplatzlizenz', 180)}
              </Group>
              <Text size="xs" c="dimmed">Diese Angaben gelten für jede Zeile, in der die Spalte fehlt oder leer ist.</Text>
              <Textarea autosize minRows={6} maxRows={16} placeholder={'KG\tBezeichnung\tEinheit\tvon\tMittel\tbis\n331\t…'} value={text} onChange={(e) => setText(e.currentTarget.value)} styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} />
              <Group gap="xs">
                <Button size="xs" variant="default" component="label">
                  CSV-Datei wählen
                  <input type="file" accept=".csv,.txt,text/csv,text/plain" hidden onChange={(e) => readFile(e.currentTarget.files?.[0])} />
                </Button>
                {paste && (
                  <Text size="sm" c={paste.rows.length ? undefined : 'dimmed'}>
                    {paste.rows.length} Zeilen lesbar{paste.problems.length ? `, ${paste.problems.length} mit Fehlern` : ''}{paste.unknown.length ? `; unbekannte Spalten: ${paste.unknown.join(', ')}` : ''}
                  </Text>
                )}
                <Button size="xs" disabled={!paste || paste.rows.length === 0} loading={add.isPending} onClick={() => paste && add.mutate(paste.rows)}>
                  {paste?.rows.length ? `${paste.rows.length} Kennwerte übernehmen` : 'Übernehmen'}
                </Button>
              </Group>
              {add.isSuccess && <Text size="sm" c="green.7">{add.data.added} Kennwerte übernommen.</Text>}
              {paste && paste.problems.length > 0 && (
                <Alert color="orange" py={6}>
                  {paste.problems.slice(0, 8).map((p) => <Text key={p.line} size="xs">Zeile {p.line}: {p.reason}</Text>)}
                  {paste.problems.length > 8 && <Text size="xs">… und {paste.problems.length - 8} weitere</Text>}
                </Alert>
              )}
              {paste && paste.rows.length > 0 && (
                <Table verticalSpacing={1} className="pst-tabular">
                  <Table.Tbody>
                    {paste.rows.slice(0, 5).map((r, i) => (
                      <Table.Tr key={i}>
                        <Table.Td w={60}><Text size="xs" ff="monospace">{r.costGroup}</Text></Table.Td>
                        <Table.Td><Text size="xs">{r.label}</Text></Table.Td>
                        <Table.Td w={60}><Text size="xs">{r.unit}</Text></Table.Td>
                        <Table.Td w={220} ta="right"><Text size="xs">{[r.low, r.mid, r.high].map((v) => (v === null ? '—' : formatAmount(v))).join(' · ')}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              {canEdit && <TextsImport onDone={refresh} />}
              {canEdit && <ObjectsImport />}
            </Stack>
          </Tabs.Panel>
        )}

        <Tabs.Panel value="regional" pt="sm"><RegionalFactorsTab canEdit={canEdit} /></Tabs.Panel>
        <Tabs.Panel value="index" pt="sm"><PriceIndexTab canEdit={canEdit} /></Tabs.Panel>
        <Tabs.Panel value="lexicon" pt="sm"><LexiconTab /></Tabs.Panel>

        {isAdmin && (
          <Tabs.Panel value="access" pt="sm">
            <AccessList />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}

/** Who besides the administration may use the catalog - a licence usually names its users. */
function AccessList(): JSX.Element {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });
  const access = useQuery({ queryKey: ['benchmarks', 'access'], queryFn: benchmarkApi.access });
  const set = useMutation({
    mutationFn: ({ userId, granted }: { userId: string; granted: boolean }) => benchmarkApi.setAccess(userId, granted),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['benchmarks', 'access'] }),
  });
  if (users.isPending || access.isPending) return <Center h={80}><Loader size="sm" /></Center>;
  if (!users.data || !access.data) return <Alert color="red">Die Liste konnte nicht geladen werden.</Alert>;
  const granted = new Set(access.data.userIds);
  return (
    <Stack gap="xs">
      <Text size="sm">Die Administration sieht den Katalog immer. Alle anderen erst, wenn sie hier freigegeben sind – etwa nur die Personen, die die Lizenz abdeckt.</Text>
      <Table verticalSpacing={3}>
        <Table.Tbody>
          {users.data.filter((u) => !u.disabledAt).map((u) => (
            <Table.Tr key={u.id}>
              <Table.Td w={40}>
                <Checkbox
                  aria-label={`Freigabe für ${u.displayName}`}
                  checked={u.role === 'admin' || granted.has(u.id)}
                  disabled={u.role === 'admin' || set.isPending}
                  onChange={(e) => set.mutate({ userId: u.id, granted: e.currentTarget.checked })}
                />
              </Table.Td>
              <Table.Td><Text size="sm">{u.displayName}</Text><Text size="xs" c="dimmed">{u.email}</Text></Table.Td>
              <Table.Td w={200}><Text size="xs" c="dimmed">{u.role === 'admin' ? 'Administration – immer' : granted.has(u.id) ? 'freigegeben' : 'nicht freigegeben'}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {set.error && <Alert color="red" py={6}>{set.error instanceof ApiError ? set.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
    </Stack>
  );
}

/** Long texts of catalogue positions (AP-88): a CSV made from the licensed volumes, for the administration. */
function TextsImport({ onDone }: { onDone: () => Promise<void> }): JSX.Element {
  const read = useMutation({ mutationFn: async (file: File) => benchmarkApi.importTexts(await file.text()), onSettled: onDone });
  return (
    <Stack gap={4} pt="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Text size="sm" fw={500}>Beschreibungen (Langtexte) einlesen</Text>
      <Text size="xs">CSV mit den Spalten <Text span ff="monospace">bki_pos;langtext;dauer;seite</Text>. Zugeordnet über die BKI-Positionsnummer in der Bemerkung; bleibt erhalten, wenn der Katalog neu eingelesen wird.</Text>
      <FileDrop accept=".csv" label="CSV-Datei hierher ziehen oder auswählen" onFiles={(files) => files[0] && read.mutate(files[0])} disabled={read.isPending} />
      {read.isSuccess && <Alert color="green" py={6}>{read.data.texts} Beschreibungen eingelesen, {read.data.matched} Kennwerten zugeordnet.</Alert>}
      {read.isError && <Alert color="red" py={6}>{read.error instanceof ApiError ? read.error.message : 'Einlesen fehlgeschlagen.'}</Alert>}
    </Stack>
  );
}

/** Example objects with photos (AP-88): JSON made from the licensed volume; the photos must be in the media folder. */
function ObjectsImport(): JSX.Element {
  const queryClient = useQueryClient();
  const read = useMutation({
    mutationFn: async (file: File) => objectsApi.import(JSON.parse(await file.text()) as BenchmarkObject[]),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['benchmark-objects'] }),
  });
  return (
    <Stack gap={4} pt="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Text size="sm" fw={500}>Beispielobjekte mit Fotos einlesen</Text>
      <Text size="xs">JSON-Datei der Objekte (Nummer, Gebäudeart, Beschreibung, Kennzahlen, Bild), z. B. aus <Text span ff="monospace" size="xs">ops/bki-objekte</Text>. Die Fotos liegen im Medienordner des Servers; ersetzt die Objekte derselben Ausgabe. Eine Nummer, die schon eine andere Ausgabe hat, wird nicht eingelesen und gemeldet.</Text>
      <FileDrop accept=".json" label="JSON-Datei hierher ziehen oder auswählen" onFiles={(files) => files[0] && read.mutate(files[0])} disabled={read.isPending} />
      {read.isSuccess && <Alert color={objectsImportMessage(read.data).color} py={6}>{objectsImportMessage(read.data).text}</Alert>}
      {read.isError && <Alert color="red" py={6}>{read.error instanceof ApiError ? read.error.message : read.error instanceof SyntaxError ? 'Keine gültige JSON-Datei.' : 'Einlesen fehlgeschlagen.'}</Alert>}
    </Stack>
  );
}
